/**
 * The dispatch panel of one purchase order (batch 8 T12c1, #3033): the state of its dispatches, the way to prepare a new one (review,
 * then queue) and the details of each. Independently renderable: it needs only the purchase order's id. T15 places it inside
 * `PurchaseOrderDetailModal`; nothing here is mounted, routed or linked from navigation.
 *
 * What a user sees follows the server's permissions exactly:
 *   - neither `purchase_orders.read` nor `purchase_orders.dispatch`: an explanation and no request at all;
 *   - `purchase_orders.read`: the state list and the state of each dispatch, never the document;
 *   - `purchase_orders.dispatch`: preview, queue, the document, the events, cancel and reconcile. It does not show the list.
 */

import { useState } from 'react';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import {
  useDispatchAccess,
  useEnqueueDispatch,
  useOrderDispatches,
} from '@/hooks/useProcurementDispatch';
import { DispatchDetailDialog } from './DispatchDetailDialog';
import { DispatchList } from './DispatchList';
import { DispatchOwnedBoundary } from './DispatchOwnedBoundary';
import { NewDispatchDialog } from './NewDispatchDialog';
import { PendingEnqueueNotice } from './PendingEnqueueNotice';
import type { DispatchChannel } from './dispatchTypes';

export type PurchaseOrderChange = { reason: 'queued' | 'cancelled' | 'resolved' };

export interface PurchaseOrderDispatchPanelProps {
  purchaseOrderId: string;
  /** Preselects the channel in the new-dispatch dialog, for example from the supplier's order mode. */
  defaultChannel?: DispatchChannel;
  /**
   * Any value that changes when the purchase order document may have changed (for example its `updatedAt`). A document the user is
   * reviewing is dropped and must be reviewed again.
   */
  documentRevision?: string | number;
  /** The server changed something about the order (a dispatch was queued, cancelled or reconciled): refresh the order's own data. */
  onPurchaseOrderChanged?: (change: PurchaseOrderChange) => void;
}

function PanelContent({
  purchaseOrderId,
  defaultChannel,
  documentRevision,
  onPurchaseOrderChanged,
}: PurchaseOrderDispatchPanelProps) {
  const { t } = useTranslation('procurementDispatch');
  const access = useDispatchAccess();
  const list = useOrderDispatches(purchaseOrderId);
  const enqueue = useEnqueueDispatch(purchaseOrderId);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  if (!access.canReadStatus && !access.canDispatch) {
    return (
      <Alert severity="info" aria-label={t('panel.title')}>
        {t('panel.noAccess')}
      </Alert>
    );
  }

  const reportOrderChanged = (reason: PurchaseOrderChange['reason']) => onPurchaseOrderChanged?.({ reason });

  return (
    <Paper variant="outlined" sx={{ p: 2 }} component="section" aria-label={t('panel.title')}>
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="h6" component="h3">
            {t('panel.title')}
          </Typography>
          {access.canDispatch && (
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={() => setCreating(true)}
              disabled={enqueue.held !== null || enqueue.isPending}
            >
              {t('panel.new')}
            </Button>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {t('panel.intro')}
        </Typography>

        {access.canDispatch && !creating && (
          <PendingEnqueueNotice
            enqueue={enqueue}
            onCheckList={access.canReadStatus ? list.refetch : undefined}
            onResult={(result) => {
              if (result.status === 'success') reportOrderChanged('queued');
            }}
          />
        )}

        {access.canReadStatus ? (
          <DispatchList read={list} onOpen={(summary) => setOpenId(summary.id)} />
        ) : (
          <Alert severity="info">{t('panel.noStatusAccess')}</Alert>
        )}
        {!access.canDispatch && <Alert severity="info">{t('panel.noDispatchAccess')}</Alert>}
      </Stack>

      {access.canDispatch && creating && (
        <NewDispatchDialog
          open
          onClose={() => setCreating(false)}
          purchaseOrderId={purchaseOrderId}
          defaultChannel={defaultChannel}
          documentRevision={documentRevision}
          onQueued={() => reportOrderChanged('queued')}
          onOpenDetails={(dispatchId) => {
            setCreating(false);
            setOpenId(dispatchId);
          }}
          onCheckList={access.canReadStatus ? list.refetch : undefined}
        />
      )}
      {openId !== null && (
        <DispatchDetailDialog
          open
          onClose={() => setOpenId(null)}
          purchaseOrderId={purchaseOrderId}
          dispatchId={openId}
          onPrepareNew={
            access.canDispatch
              ? () => {
                  setOpenId(null);
                  setCreating(true);
                }
              : undefined
          }
          onPurchaseOrderChanged={(reason) => reportOrderChanged(reason)}
        />
      )}
    </Paper>
  );
}

export function PurchaseOrderDispatchPanel(props: PurchaseOrderDispatchPanelProps) {
  // Another purchase order is another panel: drafts, the open dialogs and any review do not carry over.
  return (
    <DispatchOwnedBoundary>
      <PanelContent key={props.purchaseOrderId} {...props} />
    </DispatchOwnedBoundary>
  );
}
